import axios from "axios";
import type {
  SubcontractorInvitePublicInfo,
  SubcontractorTask,
  SubcontractorUser,
} from "../types";

/**
 * Dış kullanıcı (taşeron) oturumu için ana client'tan BAĞIMSIZ mini axios
 * instance'ı. Interceptor kullanılmaz (ana client'ın 401'de login'e
 * yönlendirmesi burada uygulanmaz); ability token her istekte localStorage'daki
 * AYRI `subcontractor_token` anahtarından okunur ve Authorization header'ına
 * yazılır — normal auth token'ı (`token`) asla ezilmez.
 */
const subcontractorApi = axios.create({
  baseURL: "/santiye", // client.ts ile aynı base; dev'te Vite proxy çalışır
  timeout: 30000,
  withCredentials: false,
  headers: { Accept: "application/json" },
});

export const SUBCONTRACTOR_TOKEN_KEY = "subcontractor_token";
export const SUBCONTRACTOR_USER_KEY = "subcontractor_user";
export const SUBCONTRACTOR_INVITE_TOKEN_KEY = "subcontractor_invite_token";

export function getSubcontractorToken(): string | null {
  return localStorage.getItem(SUBCONTRACTOR_TOKEN_KEY);
}

export function getSubcontractorUser(): SubcontractorUser | null {
  const raw = localStorage.getItem(SUBCONTRACTOR_USER_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as SubcontractorUser;
  } catch {
    return null;
  }
}

export function saveSubcontractorSession(
  token: string,
  user: SubcontractorUser,
  inviteToken: string,
): void {
  localStorage.setItem(SUBCONTRACTOR_TOKEN_KEY, token);
  localStorage.setItem(SUBCONTRACTOR_USER_KEY, JSON.stringify(user));
  localStorage.setItem(SUBCONTRACTOR_INVITE_TOKEN_KEY, inviteToken);
}

export function clearSubcontractorSession(): void {
  localStorage.removeItem(SUBCONTRACTOR_TOKEN_KEY);
  localStorage.removeItem(SUBCONTRACTOR_USER_KEY);
  localStorage.removeItem(SUBCONTRACTOR_INVITE_TOKEN_KEY);
}

/**
 * Çıkış / geçersiz token durumunda yönlendirilecek davet sayfası yolu.
 * Kabul edilmiş davet 410 ekranını gösterir (yeni davet istenmelidir).
 */
export function getSubcontractorInvitePath(): string {
  const inviteToken = localStorage.getItem(SUBCONTRACTOR_INVITE_TOKEN_KEY);
  return inviteToken ? `/subcontractor/invite/${inviteToken}` : "/login";
}

function authHeaders(): Record<string, string> {
  const token = getSubcontractorToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// --- Public davet endpoint'leri (auth'siz) ---

export async function fetchSubcontractorInviteInfo(
  token: string,
): Promise<SubcontractorInvitePublicInfo> {
  const response = await subcontractorApi.get(
    `/api/v1/subcontractor/invite/${token}`,
  );
  return response.data.data;
}

export async function acceptSubcontractorInvite(
  token: string,
  payload: { name: string; surname: string; phone: string; password: string },
): Promise<{
  token: string;
  user: SubcontractorUser;
  project: { id: number; name: string };
}> {
  const response = await subcontractorApi.post(
    `/api/v1/subcontractor/invite/${token}/accept`,
    payload,
  );
  // 201 yanıtı { message, token, user, project } — 'data' sarması yok.
  return response.data;
}

// --- Ability token korumalı endpoint'ler (EnsureSubcontractorScope whitelist) ---

export async function fetchSubcontractorTasks(): Promise<SubcontractorTask[]> {
  const response = await subcontractorApi.get(`/api/v1/subcontractor/tasks`, {
    headers: authHeaders(),
  });
  return response.data.data || [];
}

export async function updateSubcontractorTaskStatus(
  taskId: number,
  status: "yapiliyor" | "tamamlandi",
): Promise<SubcontractorTask> {
  const response = await subcontractorApi.patch(
    `/api/v1/subcontractor/tasks/${taskId}/status`,
    { status },
    { headers: authHeaders() },
  );
  return response.data.data;
}

export async function addSubcontractorTaskComment(
  taskId: number,
  comment: string,
  attachment?: File | null,
): Promise<void> {
  if (attachment) {
    const formData = new FormData();
    formData.append("comment", comment);
    formData.append("attachment", attachment);
    await subcontractorApi.post(
      `/api/v1/subcontractor/tasks/${taskId}/comments`,
      formData,
      { headers: { ...authHeaders(), "Content-Type": "multipart/form-data" } },
    );
    return;
  }
  await subcontractorApi.post(
    `/api/v1/subcontractor/tasks/${taskId}/comments`,
    { comment },
    { headers: authHeaders() },
  );
}

/** auth/logout whitelist'te; oturum zaten kapalıysa sessizce geç. */
export async function subcontractorLogout(): Promise<void> {
  try {
    await subcontractorApi.post(
      `/api/v1/auth/logout`,
      {},
      { headers: authHeaders() },
    );
  } catch {
    // Çıkış akışını engelleme.
  }
}
